/**
 * Shared types for import/export functionality
 */
import type { ReactElement } from 'react';

import type { ImportFieldSummary } from '@/lib/universalCSVParser';
import type { MinimalCard } from '@/services/cardDatabase';
import type { CollectionCard, ImportSource } from '@/types/card';
import type { ImportCard } from '@/types/schemas';

import type { DeckImportCard } from '@/features/decks/lib/deckImportExport';
import type { FormatHints } from '@/lib/formatDetection';

// ============================================================================
// Matched Import Card Types
// ============================================================================

export type DuplicateStatus = 'new' | 'duplicate';

export interface MatchedImportCard extends ImportCard {
  matched?: MinimalCard | null;
  matchType?: 'exact' | 'name' | 'fuzzy' | 'none';
  error?: string;
  /** Duplicate detection status */
  duplicateStatus?: DuplicateStatus;
  /** ID of matched collection card (for update mode) */
  matchedCollectionCardId?: string;
  /** Reference to matched collection card data */
  matchedCollectionCard?: CollectionCard;
}

// ============================================================================
// Format Option Types
// ============================================================================

/**
 * Generic format option for export/import selectors
 */
export interface FormatOption<T extends string = string> {
  value: T;
  label: string;
  description: string;
  icon: ReactElement;
}

/**
 * Export format metadata with file extension and mime type
 */
export interface ExportFormatMeta<T extends string = string> {
  value: T;
  label: string;
  extension: string;
  mimeType: string;
}

// ============================================================================
// MtgCraft Backup Types
// ============================================================================

/**
 * Base backup envelope with type discriminator
 */
export interface BackupBase<T extends string> {
  type: T;
  version: 1;
  exported_at: string;
}

/**
 * Card reference in deck backup
 */
export interface DeckCardBackup {
  id?: string;
  scryfall_id: string;
  quantity: number;
  zone: 'commander' | 'companion' | 'main' | 'sideboard' | 'maybeboard' | 'tokens';
}

/**
 * Single deck data for backup
 */
export interface DeckBackupData {
  id?: string;
  name: string;
  format: string;
  status: string;
  description?: string;
  cover_card_id?: string;
  cards: DeckCardBackup[];
}

/**
 * Single deck backup file
 */
export interface SingleDeckBackup extends BackupBase<'mtgcraft_deck'> {
  deck: DeckBackupData;
}

/**
 * Multiple decks backup file
 */
export interface MultiDeckBackup extends BackupBase<'mtgcraft_decks'> {
  decks: DeckBackupData[];
}

/**
 * Card reference in list backup
 */
export interface ListCardBackup {
  id?: string;
  oracle_id: string;
  name: string;
  scryfall_id?: string;
  quantity: number;
  priority?: 'low' | 'medium' | 'high';
  notes?: string;
}

/**
 * Single list data for backup
 */
export interface ListBackupData {
  id?: string;
  name: string;
  icon?: string;
  color?: string;
  cards: ListCardBackup[];
}

/**
 * Single list backup file
 */
export interface SingleListBackup extends BackupBase<'mtgcraft_list'> {
  list: ListBackupData;
}

/**
 * Multiple lists backup file
 */
export interface MultiListBackup extends BackupBase<'mtgcraft_lists'> {
  lists: ListBackupData[];
}

/**
 * Card reference in collection backup
 */
export interface CollectionCardBackup {
  id?: string;
  scryfall_id: string;
  quantity: number;
  finish: string;
  condition: string;
  language: string;
  purchase_price: number | null;
  purchase_currency: string | null;
  purchase_date: string | null;
  is_altered: boolean;
  is_playtest: boolean;
  is_signed: boolean;
  notes: string | null;
  folder_id: string | null;
  source_system: string;
  source_id: string | null;
}

/**
 * Folder reference in collection backup
 */
export interface CollectionFolderBackup {
  id: string;
  name: string;
  color: string | null;
  icon: string | null;
  parent_id: string | null;
  sort_order: number;
}

/**
 * Collection backup file
 */
export interface CollectionBackup extends BackupBase<'mtgcraft_collection'> {
  cards: CollectionCardBackup[];
  folders: CollectionFolderBackup[];
}

/**
 * Union type for all MtgCraft backup formats
 */
export type MtgCraftBackup = SingleDeckBackup | MultiDeckBackup | SingleListBackup | MultiListBackup | CollectionBackup;

/**
 * Type guard to check if backup is a single deck
 */
export function isSingleDeckBackup(backup: MtgCraftBackup): backup is SingleDeckBackup {
  return backup.type === 'mtgcraft_deck';
}

/**
 * Type guard to check if backup is multiple decks
 */
export function isMultiDeckBackup(backup: MtgCraftBackup): backup is MultiDeckBackup {
  return backup.type === 'mtgcraft_decks';
}

/**
 * Type guard to check if backup is a single list
 */
export function isSingleListBackup(backup: MtgCraftBackup): backup is SingleListBackup {
  return backup.type === 'mtgcraft_list';
}

/**
 * Type guard to check if backup is multiple lists
 */
export function isMultiListBackup(backup: MtgCraftBackup): backup is MultiListBackup {
  return backup.type === 'mtgcraft_lists';
}

/**
 * Type guard to check if backup is a collection
 */
export function isCollectionBackup(backup: MtgCraftBackup): backup is CollectionBackup {
  return backup.type === 'mtgcraft_collection';
}

// ============================================================================
// Import Tab Types
// ============================================================================

export type ImportTabId = 'upload' | 'paste';

export interface ImportTab {
  id: ImportTabId;
  label: string;
}

export const IMPORT_TABS: ImportTab[] = [
  { id: 'upload', label: 'Upload File' },
  { id: 'paste', label: 'Paste Text' },
];

// ============================================================================
// Import Pipeline Types
// ============================================================================

export type BackupType = 'mtgcraft_deck' | 'mtgcraft_decks' | 'mtgcraft_list' | 'mtgcraft_lists' | 'mtgcraft_collection';

/** Discriminated union for all import parse results */
export type ProcessedImport = ProcessedCSVImport | ProcessedTextImport | ProcessedBackupImport;

export interface ProcessedCSVImport {
  kind: 'csv';
  cards: ImportCard[];
  fieldSummary: ImportFieldSummary;
  detectedSource: ImportSource;
}

export interface ProcessedTextImport {
  kind: 'text';
  /** DeckImportCard[] for deck context, ImportCard[] for collection/list */
  cards: DeckImportCard[] | ImportCard[];
  hints: FormatHints;
}

export interface ProcessedBackupImport {
  kind: 'backup';
  backupType: BackupType;
  data: SingleDeckBackup | MultiDeckBackup | SingleListBackup | MultiListBackup | CollectionBackup;
}

export interface ImportPipelineOptions {
  preferredCategory?: 'collection' | 'deck' | 'list';
  forceSource?: ImportSource;
}
